import { Injectable } from '@angular/core';
import { NetzzuordnungFilter } from '../../netzzuordnung/netzzuordnung-filter';
import { Pageable } from '../../../common/paging/pageable';
import { NetzbetreiberFilter } from '../../netzbetreiber/netzbetreiber-filter';
import { ErzeugungsanlageFilter } from '../../erzeugungsanlage/erzeugungsanlage-filter';
import { PlanwertFilter } from '../../planwert/planwert-filter';

@Injectable({
    providedIn: 'root',
})
export class RoutingService {

    public static readonly ERZEUGUNGSANLAGE = 'erzeugungsanlage';
    public static readonly NETZBETREIBER = 'netzbetreiber';
    public static readonly NETZZUORDNUNG = 'netzzuordnung';
    public static readonly PLANWERT = 'planwert';

    public dashboard() {
        return '/';
    }

    public erzeugungsanlageTable(pageable?: Pageable, erzeugungsanlageFilter?: ErzeugungsanlageFilter): string {
        return '/' + RoutingService.ERZEUGUNGSANLAGE + '/list?' +
            this.pageableToQuery(pageable,
                this.filterErzeugungsanlageToQuery(erzeugungsanlageFilter))
                .toString();
    }

    public erzeugungsanlagePath(): string {
        return '/' + RoutingService.ERZEUGUNGSANLAGE + '/list';
    }

    public get netzbetreiberPath(): string {
        return `/${RoutingService.NETZBETREIBER}/list`;
    }

    public netzbetreiberTable(pageable?: Pageable, netzbetreiberFilter?: NetzbetreiberFilter): string {
        return `${this.netzbetreiberPath}?` +
            this.pageableToQuery(pageable,
                this.filterNetzbetreiberToQuery(netzbetreiberFilter))
                .toString();
    }

    public netzbetreiberDetailPath(stwId: number | string, openedInSideNav = false): string {
        return `/${RoutingService.NETZBETREIBER}/detail?stadtwerkeId=${stwId}&openedInSideNav=${openedInSideNav}`;
    }

    public get netzzuordnungPath(): string {
        return `/${RoutingService.NETZZUORDNUNG}/list`;
    }

    public planwertPath(): string {
        return '/' + RoutingService.PLANWERT + '/list';
    }

    public planwerteTable(pageable?: Pageable, filter?: PlanwertFilter): string {
        return `${this.planwertPath()}?` +
            this.pageableToQuery(pageable,
                this.filterPlanwerteToQuery(filter))
                .toString();
    }

    public netzzuordnungTable(pageable?: Pageable, netzzuordnungFilter?: NetzzuordnungFilter): string {
        return `${this.netzzuordnungPath}?` +
            this.pageableToQuery(pageable,
                this.filterNetzzuordnungenToQuery(netzzuordnungFilter))
                .toString();
    }

    private pageableToQuery(pageable?: Pageable, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (pageable) {
            if (pageable.page) {
                urlSearchParams.append('page', pageable.page + '');
            }
            if (pageable.sort) {
                urlSearchParams.append('sort', pageable.sort);
            }
            if (pageable.direction) {
                urlSearchParams.append('direction', pageable.direction);
            }
        }
        return urlSearchParams;
    }

    private filterErzeugungsanlageToQuery(filter?: ErzeugungsanlageFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.netzbetreiberName) {
                urlSearchParams.append('netzbetreiberName', '' + filter.netzbetreiberName);
            }
            if (filter.netzbetreiberTeilgebiet) {
                urlSearchParams.append('netzbetreiberTeilgebiet', '' + filter.netzbetreiberTeilgebiet);
            }
            if (filter.dokumentenId) {
                urlSearchParams.append('dokumentenId', filter.dokumentenId);
            }
            if (filter.steuerbareResourceId) {
                urlSearchParams.append('steuerbareResourceId', filter.steuerbareResourceId);
            }
            if (filter.technischeResourceId) {
                urlSearchParams.append('technischeResourceId', filter.technischeResourceId);
            }
            if (filter.bezVnbIntern) {
                urlSearchParams.append('bezVnbIntern', filter.bezVnbIntern);
            }
            if (filter.ortsname) {
                urlSearchParams.append('ortsname', filter.ortsname);
            }
            if (filter.stationsname) {
                urlSearchParams.append('stationsname', filter.stationsname);
            }
            if (filter.trafoFeldName) {
                urlSearchParams.append('trafoFeldName', filter.trafoFeldName);
            }
            if (filter.energietraeger) {
                urlSearchParams.append('energietraeger', filter.energietraeger);
            }
            if (typeof filter.status == 'boolean') {
                urlSearchParams.append('status', JSON.stringify(filter.status));
            }
            if (typeof filter.installierteLeistungMin == 'number') {
                urlSearchParams.append('installierteLeistungMin', '' + filter.installierteLeistungMin);
            }
            if (typeof filter.installierteLeistungMax == 'number') {
                urlSearchParams.append('installierteLeistungMax', '' + filter.installierteLeistungMax);
            }
        }
        return urlSearchParams;
    }

    private filterNetzbetreiberToQuery(filter?: NetzbetreiberFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.name) {
                urlSearchParams.append('name', filter.name);
            }
            if (filter.stwId) {
                urlSearchParams.append('stwId', filter.stwId);
            }
            if (filter.teilgebiet) {
                urlSearchParams.append('teilgebiet', filter.teilgebiet);
            }
            if (filter.netzbetreibernummer) {
                urlSearchParams.append('netzbetreibernummer', filter.netzbetreibernummer);
            }
            if (filter.geschaeftspartnerId) {
                urlSearchParams.append('geschaeftspartnerId', filter.geschaeftspartnerId);
            }
            if (filter.lastImportDate && (filter.lastImportDate.start || filter.lastImportDate.end)) {
                urlSearchParams.append('lastImportDate', JSON.stringify(filter.lastImportDate));
            }
            if (filter.dienstleistungsart) {
                urlSearchParams.append('dienstleistungsart', filter.dienstleistungsart);
            }
            if (filter.dienstleistungsmoduleBeschreibung && filter.dienstleistungsmoduleBeschreibung.length > 0) {
                urlSearchParams.append('dienstleistungsmodule', JSON.stringify(filter.dienstleistungsmoduleBeschreibung));
            }
        }
        return urlSearchParams;
    }

    private filterNetzzuordnungenToQuery(filter?: NetzzuordnungFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.trafoFeldName) {
                urlSearchParams.append('trafoFeldName', filter.trafoFeldName);
            }
            if (filter.ortsname) {
                urlSearchParams.append('ortsname', filter.ortsname);
            }
            if (filter.stationsname) {
                urlSearchParams.append('stationsname', filter.stationsname);
            }
            if (filter.uuid) {
                urlSearchParams.append('uuid', filter.uuid);
            }
            if (filter.technischerPlatz) {
                urlSearchParams.append('technischerPlatz', filter.technischerPlatz);
            }
            if (filter.netzbetreiberName) {
                urlSearchParams.append('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.teilgebiet) {
                urlSearchParams.append('teilgebiet', filter.teilgebiet);
            }
            if (filter.stadtwerkeId) {
                urlSearchParams.append('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.netzgruppe) {
                urlSearchParams.append('netzgruppe', filter.netzgruppe);
            }
            if (filter.uuidDateStart) {
                urlSearchParams.append('uuidDateStart', filter.uuidDateStart.toISOString(true));
            }
            if (filter.uuidDateEnd) {
                urlSearchParams.append('uuidDateEnd', filter.uuidDateEnd.toISOString(true));
            }
        }
        return urlSearchParams;
    }

    private filterPlanwerteToQuery(filter?: PlanwertFilter, urlSearchParams = new URLSearchParams()): URLSearchParams {
        if (filter) {
            if (filter.netzbetreiberName) {
                urlSearchParams.append('netzbetreiberName', filter.netzbetreiberName);
            }
            if (filter.stadtwerkeId) {
                urlSearchParams.append('stadtwerkeId', filter.stadtwerkeId);
            }
            if (filter.erzeugungsanlageDokumentenId) {
                urlSearchParams.append('erzeugungsanlageDokumentenId', filter.erzeugungsanlageDokumentenId);
            }
            if (filter.erzeugungsanlageTechnischeResourceId) {
                urlSearchParams.append('erzeugungsanlageTechnischeResourceId', filter.erzeugungsanlageTechnischeResourceId);
            }
            if (filter.erzeugungsanlageBezVnbIntern) {
                urlSearchParams.append('erzeugungsanlageBezVnbIntern', filter.erzeugungsanlageBezVnbIntern);
            }
            if (filter.netzzuordnungNetzverknuepfungspunkte) {
                urlSearchParams.append('netzzuordnungNetzverknuepfungspunkte', filter.netzzuordnungNetzverknuepfungspunkte);
            }
            if (filter.dataTransferDate?.start) {
                urlSearchParams.append('dataTransferDateStart', filter.dataTransferDate.start.toISOString(true));
            }
            if (filter.dataTransferDate?.end) {
                urlSearchParams.append('dataTransferDateEnd', filter.dataTransferDate.end.toISOString(true));
            }
            if (filter.type) {
                urlSearchParams.append('type',  JSON.stringify(filter.type));
            }
            if (filter.status) {
                urlSearchParams.append('status',  JSON.stringify(filter.status));
            }
        }
        return urlSearchParams;
    }
}

